import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "valueObject"]
  static values = { paths: Array, labels: Array, selected: Array, delimiter: { type: String, default: "|" } }

  connect() {
    this.renderSegment(0)
    if (this.selectedValue.length > 0) {
      this.preselectValues()
    }
  }

  preselectValues() {
    const selected = this.selectedValue
    for (let i = 0; i < selected.length; i++) {
      const selectEl = this.containerTarget.querySelectorAll("select")[i]
      if (!selectEl) break
      selectEl.value = selected[i]
      if (i < selected.length - 1) {
        this.renderSegment(i + 1)
      }
    }
    // Check if auto-collapse needed after last selected segment
    this.autoCollapseFrom(selected.length - 1)
    this.updateHiddenFields()
  }

  segmentChanged(event) {
    const select = event.target
    const level = parseInt(select.dataset.segmentIndex, 10)

    // Remove all selects after this level
    const selects = this.containerTarget.querySelectorAll("select")
    for (let i = selects.length - 1; i > level; i--) {
      selects[i].closest(".segmented-select-segment").remove()
    }

    if (select.value === "") {
      this.updateHiddenFields()
      return
    }

    // Check if any paths continue beyond this level
    const selections = this.getSelections()
    const nextOptions = this.getOptionsForLevel(level + 1, selections)

    if (nextOptions.length > 0) {
      this.renderSegment(level + 1)
      this.autoCollapseFrom(level + 1)
    }

    this.updateHiddenFields()
  }

  renderSegment(level) {
    const selections = this.getSelections()
    const options = this.getOptionsForLevel(level, selections)

    if (options.length === 0) return

    const label = this.labelsValue[level] || `Segment ${level + 1}`
    const wrapper = document.createElement("div")
    wrapper.className = "segmented-select-segment"
    wrapper.style.marginBottom = "5px"

    const selectEl = document.createElement("select")
    selectEl.className = "form-control"
    selectEl.dataset.action = "change->segmented-select#segmentChanged"
    selectEl.dataset.segmentIndex = level

    const blankOption = document.createElement("option")
    blankOption.value = ""
    const selectPrompt = (window.I18n["custom_field_definitions.segment_config.select_prompt"] || "Select %{label}...").replace("%{label}", label)
    blankOption.textContent = selectPrompt
    selectEl.appendChild(blankOption)

    options.forEach(opt => {
      const option = document.createElement("option")
      option.value = opt
      option.textContent = opt
      selectEl.appendChild(option)
    })

    wrapper.appendChild(selectEl)
    this.containerTarget.appendChild(wrapper)
  }

  autoCollapseFrom(level) {
    let selects = this.containerTarget.querySelectorAll("select")
    let currentLevel = level

    while (currentLevel < selects.length) {
      const selectEl = selects[currentLevel]
      // Count non-blank options
      const nonBlankOptions = Array.from(selectEl.options).filter(o => o.value !== "")

      if (nonBlankOptions.length === 1 && selectEl.value === "") {
        selectEl.value = nonBlankOptions[0].value
        selectEl.disabled = true

        // Check if next level exists
        const selections = this.getSelections()
        const nextOptions = this.getOptionsForLevel(currentLevel + 1, selections)
        if (nextOptions.length > 0) {
          this.renderSegment(currentLevel + 1)
        }
      } else {
        break
      }
      currentLevel++
      // Re-query selects since we may have added new ones
      selects = this.containerTarget.querySelectorAll("select")
      if (currentLevel >= selects.length) break
    }
  }

  getSelections() {
    const selects = this.containerTarget.querySelectorAll("select")
    return Array.from(selects).map(s => s.value).filter(v => v !== "")
  }

  getOptionsForLevel(level, selections) {
    const matching = this.pathsValue.filter(path => {
      if (path.length <= level) return false
      return selections.every((sel, i) => i >= level || path[i] === sel)
    })
    const unique = [...new Set(matching.map(path => path[level]))]
    return unique
  }

  updateHiddenFields() {
    const selections = this.getSelections()
    this.valueObjectTarget.innerHTML = ""

    const namePrefix = this.valueObjectTarget.dataset.namePrefix

    selections.forEach(val => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `${namePrefix}[value_object][]`
      input.value = val
      this.valueObjectTarget.appendChild(input)
    })

    // Also set the display value
    const delimiter = this.delimiterValue
    const displayInput = document.createElement("input")
    displayInput.type = "hidden"
    displayInput.name = `${namePrefix}[value]`
    displayInput.value = selections.length > 0 ? selections.join(` ${delimiter} `) : ""
    this.valueObjectTarget.appendChild(displayInput)
  }
}
